import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatCard } from "../components/shared";
import { formatMoney, formatNum } from "../lib/constants";
import {
  TrendingUp, ShoppingCart, Receipt, Wallet, Boxes, Layers, AlertTriangle, Factory,
  ArrowDownLeft, ArrowUpRight, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const AXIS_TICK = { fontFamily: "IBM Plex Mono", fontSize: 11 };
const TOOLTIP_STYLE = { fontFamily: "IBM Plex Mono", fontSize: 12, borderRadius: 0, border: "1px solid hsl(var(--border))" };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const cur = user?.organization?.base_currency || "NGN";

  const load = useCallback(async () => {
    try { setData((await api.get("/dashboard")).data); }
    catch { toast.error("Failed to load dashboard data"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="font-mono text-sm text-muted-foreground animate-pulse">Loading dashboard…</div>;

  const has = (k) => data[k] !== undefined;
  const finCards = [];
  if (has("total_sales")) finCards.push(<StatCard key="s" index={finCards.length} label="Total Sales" value={formatMoney(data.total_sales, cur)} icon={TrendingUp} accent sub={`${data.invoice_count} invoices`} />);
  if (has("total_purchases")) finCards.push(<StatCard key="p" index={finCards.length} label="Purchases" value={formatMoney(data.total_purchases, cur)} icon={ShoppingCart} sub={`${data.po_count} orders`} />);
  if (has("total_expenses")) finCards.push(<StatCard key="e" index={finCards.length} label="Expenses (approved)" value={formatMoney(data.total_expenses, cur)} icon={Receipt} sub={data.pending_expense_count ? `${data.pending_expense_count} pending approval` : "all cleared"} />);
  if (has("net_profit")) finCards.push(<StatCard key="n" index={finCards.length} label="Net Profit" value={formatMoney(data.net_profit, cur)} icon={Wallet} sub="Admin only" />);

  const invCards = [];
  if (has("raw_material_count")) {
    invCards.push(<StatCard key="rm" index={invCards.length} label="Raw Materials" value={formatNum(data.raw_material_count, 0)} icon={Boxes} sub={`${formatMoney(data.raw_stock_value, cur)} value`} />);
    invCards.push(<StatCard key="pr" index={invCards.length} label="Products" value={formatNum(data.product_count, 0)} icon={Layers} />);
    invCards.push(<StatCard key="fu" index={invCards.length} label="Finished Units" value={formatNum(data.total_finished_units, 0)} icon={Factory} />);
    invCards.push(<StatCard key="ls" index={invCards.length} label="Low Stock Alerts" value={formatNum((data.low_stock || []).length, 0)} icon={AlertTriangle} />);
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle={`Overview for ${user?.organization?.name}`} />

      {finCards.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">{finCards}</div>}

      {has("payments_in") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <StatCard index={0} label="Money In" value={formatMoney(data.payments_in, cur)} icon={ArrowDownLeft} accent />
          <StatCard index={1} label="Money Out" value={formatMoney(data.payments_out, cur)} icon={ArrowUpRight} />
        </div>
      )}

      {invCards.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">{invCards}</div>}

      {(has("sales_trend") || has("low_stock")) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {has("sales_trend") && (
            <div className="lg:col-span-2 border border-border bg-card p-5">
              <h3 className="font-display text-lg font-bold mb-4">Sales Trend</h3>
              {data.sales_trend.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No sales data yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.sales_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={AXIS_TICK} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="sales" fill="hsl(var(--primary))" maxBarSize={72} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
          {has("low_stock") && (
            <div className="border border-border bg-card p-5">
              <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-destructive" /> Low Stock</h3>
              {data.low_stock.length === 0 ? (
                <p className="text-sm text-muted-foreground">All raw materials above reorder level.</p>
              ) : (
                <div className="space-y-2">
                  {data.low_stock.slice(0, 8).map((r) => (
                    <div key={r.id} className="flex items-center justify-between border-b border-border pb-2">
                      <span className="text-sm font-medium truncate">{r.name}</span>
                      <span className="font-mono text-sm text-destructive">{formatNum(r.quantity)} {r.unit}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {has("pending_expense_count") && data.pending_expense_count > 0 && (
        <div className="border border-border bg-card p-4 mt-4 flex items-center gap-3" data-testid="pending-expenses-alert">
          <Clock className="w-5 h-5 text-amber-500" />
          <span className="text-sm"><b>{data.pending_expense_count}</b> expense request(s) awaiting your approval.</span>
          <a href="/expenses" className="text-sm text-primary underline ml-auto">Review</a>
        </div>
      )}

      {has("recent_invoices") && (
        <div className="border border-border bg-card p-5 mt-4">
          <h3 className="font-display text-lg font-bold mb-4">Recent Invoices</h3>
          {data.recent_invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No invoices yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {data.recent_invoices.map((i) => (
                <div key={i.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <div className="font-mono text-sm font-semibold">{i.number}</div>
                    <div className="text-xs text-muted-foreground">{i.customer_name}</div>
                  </div>
                  <div className="font-mono text-sm font-bold">{formatMoney(i.total, i.currency)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
